/**
 * Hooks TanStack Query — POD (S5.6 PR3 → PR-C).
 *
 * Pattern aligné sur `useEligibility.ts` / `useDeliverableSettings.ts`.
 *
 * Le polling 5s sur `useVariantPodStatus` est **conditionnel** : on ne
 * burn pas la batterie de Stéphanie ni la queue HTTP en interrogeant le
 * BE quand la row est `ready`/`failed`/`archived` (terminaux). Seul
 * `draft`/`uploading` déclenche le refetch interval.
 *
 * S5.6 PR-C — `usePodProviders`, `usePodConnections`, `usePodConnect`,
 * `usePodDisconnect` supprimés. La voie UI portail Gelato a été
 * démantelée — la clé Gelato vit en `.env` de prod.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { podApi } from '@/utils/api/pod'
import type { PodPreparePayload, PodProductResponse } from '@/types/pod'

interface ApiError {
  detail: string
  status?: number
}

export const podKeys = {
  all: ['pod'] as const,
  variantStatus: (variantId: number) => [...podKeys.all, 'variants', variantId] as const,
}

/** Polling pendant la préparation (status `draft` ou `uploading`). 5s
 * équilibre réactivité UI et charge HTTP — aligné sur le plan S5.6. */
const ACTIVE_PREPARE_POLL_MS = 5_000

// ──────────────────────────────────────────────────────────────────────────────
// Variant-scoped
// ──────────────────────────────────────────────────────────────────────────────

export function useVariantPodStatus(variantId: number | null | undefined) {
  return useQuery<PodProductResponse | null>({
    queryKey: podKeys.variantStatus(variantId ?? 0),
    queryFn: () => podApi.getVariantStatus(variantId!),
    enabled: variantId != null && variantId > 0,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      // `draft` est transitoire (le service écrit immédiatement
      // `uploading` après l'INSERT). On garde quand même le poll pour
      // ne pas rater une row stuck en draft.
      if (data.status === 'draft' || data.status === 'uploading') {
        return ACTIVE_PREPARE_POLL_MS
      }
      return false
    },
  })
}

interface PrepareVariantVars {
  variantId: number
  payload: PodPreparePayload
}

export function usePrepareVariantForPod() {
  const qc = useQueryClient()
  return useMutation<PodProductResponse, ApiError, PrepareVariantVars>({
    mutationFn: ({ variantId, payload }) => podApi.prepareVariant(variantId, payload),
    onSuccess: (data, { variantId }) => {
      qc.setQueryData(podKeys.variantStatus(variantId), data)
    },
  })
}

export function useArchiveVariantPod() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, number>({
    mutationFn: podApi.archiveVariant,
    onSuccess: (_, variantId) => {
      // Pose `null` immédiatement — l'UI bascule sur "non préparé" sans
      // attendre un refetch.
      qc.setQueryData(podKeys.variantStatus(variantId), null)
    },
  })
}
